/**
 * @file Tabs 页签
 * @description 紧凑页签切换。
 *
 * @whenToUse 详情分区、设置分组。
 * @whenNotToUse 路由级导航（用菜单 / Link）。
 */

import { createContext, useContext, type ReactNode } from 'react'
import { cn } from '@/lib/cn.ts'

type TabsContextValue = {
  value: string
  onChange: (value: string) => void
}

const TabsContext = createContext<TabsContextValue | null>(null)

export type TabsProps = {
  value: string
  onChange: (value: string) => void
  children: ReactNode
  className?: string
}

export function Tabs({ value, onChange, children, className }: TabsProps) {
  return (
    <TabsContext.Provider value={{ value, onChange }}>
      <div className={cn('flex flex-col gap-2', className)}>{children}</div>
    </TabsContext.Provider>
  )
}

export function TabsList({
  children,
  className,
}: {
  children: ReactNode
  className?: string
}) {
  return (
    <div
      role="tablist"
      className={cn(
        'inline-flex h-8 items-center gap-1 rounded-lg border border-border bg-surface p-0.5',
        className,
      )}
    >
      {children}
    </div>
  )
}

export function TabsTrigger({
  value,
  children,
  className,
}: {
  value: string
  children: ReactNode
  className?: string
}) {
  const ctx = useContext(TabsContext)
  if (!ctx) throw new Error('TabsTrigger must be used within Tabs')
  const active = ctx.value === value
  return (
    <button
      type="button"
      role="tab"
      aria-selected={active}
      className={cn(
        'inline-flex h-full items-center rounded-md px-2 text-sm transition-opacity duration-200',
        active
          ? 'border border-border bg-raised font-medium text-foreground'
          : 'border border-transparent text-muted hover:opacity-90',
        className,
      )}
      onClick={() => ctx.onChange(value)}
    >
      {children}
    </button>
  )
}

export function TabsContent({
  value,
  children,
  className,
}: {
  value: string
  children: ReactNode
  className?: string
}) {
  const ctx = useContext(TabsContext)
  if (!ctx) throw new Error('TabsContent must be used within Tabs')
  if (ctx.value !== value) return null
  return <div className={cn('text-sm', className)}>{children}</div>
}
